import React, { useRef } from 'react';
import styled from 'styled-components';
import { string, func, bool } from 'prop-types';
import Icon from 'TMComponents/Icons/Search';
import { AnchorLink } from 'gatsby-plugin-anchor-links';
import isEnterKeyPressed from '../../../utils/isEnterKeyPressed';

const Wrapper = styled.div`
    position: relative;
    ${({ isSearch }) =>
        isSearch ? 'width: 100%;' : 'min-width: 300px; max-width: 360px;'}

    ${({ isSearch }) =>
        !isSearch &&
        `
        input:focus + label {
            font-weight: 500;
            font-size: 0.875rem;
            line-height: 1.375rem;
            top: 10px;
        }
    `}
`;

const StyledInput = styled.input`
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.5rem;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    background-color: ${({ search, theme }) =>
        search ? theme.colors.greys.light : theme.colorSystem.primary[3]};
    border: ${({ search }) =>
        search ? 'none' : '1px solid rgba(34, 34, 34, 0.1)'};
    border-radius: ${({ search }) => (search ? '60px' : '5px')};
    padding: ${({ search }) =>
        search ? '8px 16px 8px 44px' : '29px 18px 11px'};
    position: relative;
    text-align: left;
    width: ${({ search }) => (search ? '100%' : 'calc(100% - 40px)')};
    ${({ search }) => search && 'box-sizing: border-box;'}
    ${({ search, theme }) =>
        !search && `box-shadow: inset ${theme.colorSystem.shadows['6']};`}
    /* istanbul ignore next */
    &:focus {
        outline: none;
    }
    &:disabled {
        background-color: ${({ theme }) => theme.colors.greys.light};
    }
    ${({ search, theme }) =>
        search &&
        `
        &::placeholder {
            color: ${theme.colorSystem.primary[1]};
            opacity: 1;
            font-size: 1rem;
            font-weight: 700;
            line-height: 1.5rem;
        }
    `}
`;
const Label = styled.label`
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.5rem;
    color: ${({ theme }) => theme.colorSystem.UIText[2]};
    cursor: text;
    left: 18px;
    position: absolute;
    top: 35%;
    transition: all 0.25s;
    ${({ hasContent }) => hasContent && 'top: 10px;'}
    ${({ hasContent }) =>
        hasContent &&
        `
        font-weight: 500;
        font-size: 0.875rem;
        line-height: 1.375rem;
    `}
    ${({ search }) => search && 'display: none;'}
`;

const Search = styled(Icon)`
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;

    svg {
        width: 19px;
        height: 19px;
        display: block;
    }

    path,
    rect {
        stroke: ${({ theme }) => theme.colorSystem.primary[1]};
    }
`;

const Input = ({
    placeholder,
    value,
    onChange,
    onKeyUp,
    search,
    isHelpAndSupport,
    ...rest
}) => {
    const inputEl = useRef(null);
    const focus = () => {
        inputEl.current.focus();
    };
    return (
        <Wrapper isSearch={search}>
            {search &&
                (isHelpAndSupport ? (
                    <AnchorLink
                        to="/help-support#article-help"
                        title="Help and Support Articles">
                        <Search className="article-help" />
                    </AnchorLink>
                ) : (
                    <Search />
                ))}
            <StyledInput
                {...rest}
                ref={inputEl}
                id={placeholder}
                placeholder={placeholder}
                value={value}
                search={search}
                onChange={(e) => {
                    focus(e);
                    onChange(e);
                }}
                onKeyPress={focus}
                onClick={focus}
                onKeyUp={(e) => {
                    onKeyUp(e);
                }}
            />
            {!search && (
                <Label hasContent={value.length > 0} htmlFor={placeholder}>
                    {placeholder}
                </Label>
            )}
        </Wrapper>
    );
};

Input.propTypes = {
    placeholder: string.isRequired,
    value: string.isRequired,
    onChange: func.isRequired,
    onKeyUp: func,
    search: bool,
    isHelpAndSupport: bool,
    type: string
};

Input.defaultProps = {
    onKeyUp: isEnterKeyPressed,
    search: false,
    isHelpAndSupport: false,
    type: 'text'
};

export default Input;
